import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Eye, Heart, Star, MessageSquare, Loader2, Edit, Trash2, Upload, Image, X, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const VenueDashboard = () => {
  const navigate = useNavigate();
  const { user, isVenue } = useAuth();
  const { t } = useLanguage();
  
  const [activeTab, setActiveTab] = useState('activities');
  const [activities, setActivities] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    why_families_love_it: '',
    category: '',
    age_suitability: [],
    price_range: 'mid-range',
    indoor_outdoor: 'both',
    address: '',
    city: '',
    postcode: '',
    latitude: 50.8503,
    longitude: 4.3517,
    opening_times: '',
    website: '',
    phone: '',
    parking_info: '',
    accessibility_info: '',
    buggy_friendly: false,
    grandparent_friendly: false,
    images: [],
    tags: []
  });

  useEffect(() => {
    if (!isVenue) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isVenue, navigate]);

  const fetchData = async () => {
    try {
      const [activitiesRes, analyticsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/api/venue/activities`, { withCredentials: true }),
        axios.get(`${API}/api/venue/analytics`, { withCredentials: true }),
        axios.get(`${API}/api/categories`)
      ]);
      setActivities(activitiesRes.data);
      setAnalytics(analyticsRes.data);
      setCategories(categoriesRes.data.categories);
    } catch (error) {
      console.error('Error fetching venue data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/api/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, `${API}${response.data.url}`]
      }));
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingActivity) {
        await axios.put(`${API}/api/activities/${editingActivity.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/activities`, formData, { withCredentials: true });
      }
      
      setShowAddForm(false);
      setEditingActivity(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (activity) => {
    setFormData({
      title: activity.title || '',
      summary: activity.summary || '',
      why_families_love_it: activity.why_families_love_it || '',
      category: activity.category || '',
      age_suitability: activity.age_suitability || [],
      price_range: activity.price_range || 'mid-range',
      indoor_outdoor: activity.indoor_outdoor || 'both',
      address: activity.address || '',
      city: activity.city || '',
      postcode: activity.postcode || '',
      latitude: activity.latitude || 50.8503,
      longitude: activity.longitude || 4.3517,
      opening_times: activity.opening_times || '',
      website: activity.website || '',
      phone: activity.phone || '',
      parking_info: activity.parking_info || '',
      accessibility_info: activity.accessibility_info || '',
      buggy_friendly: activity.buggy_friendly || false,
      grandparent_friendly: activity.grandparent_friendly || false,
      images: activity.images || [],
      tags: activity.tags || []
    });
    setEditingActivity(activity);
    setShowAddForm(true);
  };

  const handleDelete = async (activityId) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;
    
    try {
      await axios.delete(`${API}/api/activities/${activityId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      summary: '',
      why_families_love_it: '',
      category: '',
      age_suitability: [],
      price_range: 'mid-range',
      indoor_outdoor: 'both',
      address: '',
      city: '',
      postcode: '',
      latitude: 50.8503,
      longitude: 4.3517,
      opening_times: '',
      website: '',
      phone: '',
      parking_info: '',
      accessibility_info: '',
      buggy_friendly: false,
      grandparent_friendly: false,
      images: [],
      tags: []
    });
  };

  const toggleAgeGroup = (age) => {
    setFormData(prev => ({
      ...prev,
      age_suitability: prev.age_suitability.includes(age)
        ? prev.age_suitability.filter(a => a !== age)
        : [...prev.age_suitability, age]
    }));
  };

  const ageGroups = ['0-2', '3-5', '6-8', '9-12', '13+'];
  const priceRanges = ['free', 'budget', 'mid-range', 'premium'];

  if (!isVenue) return null;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#2C302E]/5 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/profile')}
                className="w-10 h-10 rounded-full bg-[#F9F8F6] flex items-center justify-center"
                data-testid="venue-back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-[#2C302E]" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-[#2C302E] font-['Outfit']">{t('venue_title')}</h1>
                <p className="text-sm text-[#666B67]">{t('venue_subtitle')}</p>
              </div>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setEditingActivity(null);
                setShowAddForm(true);
              }}
              className="btn-primary"
              data-testid="add-activity-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('venue_add_activity')}
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            {['activities', 'analytics'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`tab-pill capitalize ${activeTab === tab ? 'tab-pill-active' : 'tab-pill-inactive'}`}
                data-testid={`venue-tab-${tab}`}
              >
                {tab === 'activities' ? t('venue_my_activities') : t('venue_analytics')}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#E07A5F]" />
          </div>
        ) : (
          <>
            {/* Analytics Tab */}
            {activeTab === 'analytics' && analytics && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-[#E07A5F]/10 flex items-center justify-center">
                      <Eye className="w-5 h-5 text-[#E07A5F]" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-[#2C302E]">{analytics.total_views}</p>
                  <p className="text-sm text-[#666B67]">{t('venue_total_views')}</p>
                </div>
                
                <div className="bg-white rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-[#81B29A]/10 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-[#81B29A]" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-[#2C302E]">{analytics.total_saves}</p>
                  <p className="text-sm text-[#666B67]">{t('venue_total_saves')}</p>
                </div>
                
                <div className="bg-white rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-[#2C302E]">{analytics.total_reviews}</p>
                  <p className="text-sm text-[#666B67]">{t('venue_total_reviews')}</p>
                </div>
                
                <div className="bg-white rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                      <Star className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-[#2C302E]">{analytics.avg_rating || '-'}</p>
                  <p className="text-sm text-[#666B67]">{t('venue_avg_rating')}</p>
                </div>
              </div>
            )}

            {/* Activities Tab */}
            {activeTab === 'activities' && (
              <div>
                {activities.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-[#E07A5F]/10 flex items-center justify-center mx-auto mb-4">
                      <Plus className="w-8 h-8 text-[#E07A5F]" />
                    </div>
                    <h2 className="text-xl font-semibold text-[#2C302E] mb-2">{t('venue_no_activities')}</h2>
                    <p className="text-[#666B67] mb-6">{t('venue_add_first')}</p>
                    <Button
                      onClick={() => setShowAddForm(true)}
                      className="btn-primary"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('venue_add_activity')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activities.map((activity) => (
                      <div key={activity.id} className="bg-white rounded-2xl p-4 flex gap-4">
                        <img 
                          src={activity.images?.[0] || 'https://via.placeholder.com/100'} 
                          alt={activity.title}
                          className="w-24 h-24 rounded-xl object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-medium text-[#2C302E]">{activity.title}</h3>
                              <p className="text-sm text-[#666B67]">{activity.city}</p>
                            </div>
                            <span className={`badge ${
                              activity.status === 'approved' ? 'badge-secondary' :
                              activity.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {activity.status === 'approved' ? t('venue_approved') :
                               activity.status === 'pending' ? t('venue_pending') :
                               t('venue_rejected')}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-4 mt-3 text-sm text-[#666B67]">
                            <span className="flex items-center gap-1">
                              <Eye className="w-4 h-4" />
                              {activity.views_count}
                            </span>
                            <span className="flex items-center gap-1">
                              <Heart className="w-4 h-4" />
                              {activity.saves_count}
                            </span>
                            <span className="flex items-center gap-1">
                              <Star className="w-4 h-4" />
                              {activity.avg_rating || '-'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(activity)}
                            data-testid={`edit-${activity.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(activity.id)}
                            className="text-red-500 hover:bg-red-50"
                            data-testid={`delete-${activity.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Add/Edit Activity Dialog */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingActivity ? t('venue_form_update') : t('venue_add_activity')}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2C302E] mb-2">{t('venue_form_title')} *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  required
                  className="input-field"
                  data-testid="activity-title-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#2C302E] mb-2">{t('venue_form_summary')} *</label>
                <Textarea
                  value={formData.summary}
                  onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
                  required
                  className="min-h-[80px]"
                  data-testid="activity-summary-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#2C302E] mb-2">{t('venue_form_why_love')}</label>
                <Textarea
                  value={formData.why_families_love_it}
                  onChange={(e) => setFormData(prev => ({ ...prev, why_families_love_it: e.target.value }))}
                  className="min-h-[60px]"
                />
              </div>
            </div>

            {/* Category & Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2C302E] mb-2">{t('venue_form_category')} *</label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#2C302E] mb-2">{t('venue_form_type')}</label>
                <Select
                  value={formData.indoor_outdoor}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, indoor_outdoor: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="indoor">Indoor</SelectItem>
                    <SelectItem value="outdoor">Outdoor</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Age & Price */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2C302E] mb-2">{t('venue_form_ages')} *</label>
                <div className="flex flex-wrap gap-2">
                  {ageGroups.map((age) => (
                    <button
                      key={age}
                      type="button"
                      onClick={() => toggleAgeGroup(age)}
                      className={`px-3 py-1 rounded-full text-sm ${
                        formData.age_suitability.includes(age)
                          ? 'bg-[#E07A5F] text-white'
                          : 'bg-[#F9F8F6] text-[#666B67]'
                      }`}
                    >
                      {age}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#2C302E] mb-2">{t('venue_form_price')}</label>
                <Select
                  value={formData.price_range}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, price_range: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priceRanges.map((price) => (
                      <SelectItem key={price} value={price} className="capitalize">{price}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2C302E] mb-2">{t('venue_form_address')} *</label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  required
                  className="input-field"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2C302E] mb-2">{t('venue_form_city')} *</label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    required
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C302E] mb-2">{t('venue_form_postcode')}</label>
                  <Input
                    value={formData.postcode}
                    onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value }))}
                    className="input-field"
                  />
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2C302E] mb-2">{t('venue_form_website')}</label>
                <Input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C302E] mb-2">{t('venue_form_phone')}</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="input-field"
                />
              </div>
            </div>

            {/* Opening Times */}
            <div>
              <label className="block text-sm font-medium text-[#2C302E] mb-2">{t('venue_form_opening')}</label>
              <Input
                value={formData.opening_times}
                onChange={(e) => setFormData(prev => ({ ...prev, opening_times: e.target.value }))}
                placeholder="e.g., 10:00 - 18:00"
                className="input-field"
              />
            </div>

            {/* Features */}
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.buggy_friendly}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, buggy_friendly: checked }))}
                />
                <span className="text-sm text-[#2C302E]">{t('venue_form_buggy')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.grandparent_friendly}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, grandparent_friendly: checked }))}
                />
                <span className="text-sm text-[#2C302E]">{t('venue_form_grandparent')}</span>
              </label>
            </div>

            {/* Images */}
            <div>
              <label className="block text-sm font-medium text-[#2C302E] mb-2">{t('common_add_photos')}</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.images.map((img, index) => (
                  <div key={index} className="relative w-20 h-20">
                    <img src={img} alt="" className="w-full h-full object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 border-2 border-dashed border-[#2C302E]/20 rounded-lg flex items-center justify-center cursor-pointer hover:border-[#E07A5F] transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                  {uploadingImage ? (
                    <Loader2 className="w-6 h-6 animate-spin text-[#666B67]" />
                  ) : (
                    <Image className="w-6 h-6 text-[#666B67]" />
                  )}
                </label>
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingActivity(null);
                  resetForm();
                }}
                className="flex-1"
              >
                {t('detail_cancel')}
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 btn-primary"
                data-testid="submit-activity-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {editingActivity ? t('venue_form_update') : t('venue_form_submit')}
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VenueDashboard;
